import React, { useEffect, useMemo, useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import { supabase } from '../../config/supabase';
import { ownerService } from '../../services/ownerService';
import { progressStageSettingsService } from '../../services/progressStageSettingsService';
import { progressStageService, ProgressStage } from '../../services/progressStageService';

interface ProgressStageSettingsProps {
  onClose?: () => void;
}

const ProgressStageSettings: React.FC<ProgressStageSettingsProps> = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [orgId, setOrgId] = useState<number | null>(null);
  const [stages, setStages] = useState<ProgressStage[]>([]);
  const [editing, setEditing] = useState<Record<number, { label: string; value: number }>>({});
  const [hasCustomSettings, setHasCustomSettings] = useState<boolean>(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const currentOrgIdString = await ownerService.getOwnerOrgId();
      if (!currentOrgIdString) {
        setError('Unable to determine organization ID');
        return;
      }
      const currentOrgId = Number(currentOrgIdString);
      if (isNaN(currentOrgId)) {
        setError('Invalid organization ID');
        return;
      }
      setOrgId(currentOrgId);

      // Fetch stages (DB if present, otherwise defaults)
      const loaded = await progressStageSettingsService.getStages(currentOrgId);
      setStages(loaded);

      // Check if custom settings exist in DB
      const { data: anyRow } = await supabase
        .from('progress_stage_settings')
        .select('id')
        .eq('org_id', currentOrgId)
        .limit(1)
        .maybeSingle();
      setHasCustomSettings(!!anyRow);
    } catch (e: any) {
      setError(e.message || 'Failed to load progress stage settings');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (value: number) => {
    const stage = stages.find(s => s.value === value);
    if (!stage) return;
    setEditing(prev => ({ ...prev, [value]: { label: stage.label, value: stage.value } }));
  };

  const handleCancel = (value: number) => {
    setEditing(prev => {
      const copy = { ...prev };
      delete copy[value];
      return copy;
    });
  };

  const handleChange = (value: number, newLabel: string) => {
    setEditing(prev => ({ ...prev, [value]: { ...(prev[value] || { value }), label: newLabel } }));
  };

  const handleChangeValue = (originalValue: number, newValueString: string) => {
    const num = Number(newValueString);
    if (!Number.isFinite(num) || num < 0) {
      // Ignore invalid numeric entries here; final validation on apply
      setEditing(prev => ({ ...prev, [originalValue]: { ...(prev[originalValue] || { label: '' }), value: NaN as any } }));
      return;
    }
    setEditing(prev => ({ ...prev, [originalValue]: { ...(prev[originalValue] || { label: '' }), value: num } }));
  };

  // Apply edit locally (do not persist until Save Changes)
  const handleApplyEditLocal = (value: number) => {
    const edit = editing[value];
    if (!edit) return;
    const desiredValue = edit.value;
    const desiredLabel = edit.label.trim() || `Stage ${desiredValue}`;

    if (!Number.isFinite(desiredValue) || desiredValue < 0) {
      setError('Stage value must be a non-negative number');
      return;
    }
    // Ensure unique value
    const duplicate = stages.some(s => s.value === desiredValue && s.value !== value);
    if (duplicate) {
      setError('Stage value must be unique');
      return;
    }

    setStages(prev => {
      // Update label and possibly value (which is the identity key)
      const updated = prev.map(s => {
        if (s.value === value) {
          return { ...s, value: desiredValue, label: desiredLabel };
        }
        return s;
      });
      // Sort by current ordering (keep order, only identity changed)
      return updated;
    });
    handleCancel(value);
  };

  // Remove locally (persist on Save Changes)
  const handleRemoveLocal = (value: number) => {
    setStages(prev => prev.filter(s => s.value !== value));
    setEditing(prev => {
      const copy = { ...prev };
      delete copy[value];
      return copy;
    });
  };

  const handleAdd = () => {
    const nextValue = stages.length > 0 ? Math.max(...stages.map(s => s.value)) + 1 : 0;
    setStages(prev => [...prev, { value: nextValue, label: `Stage ${nextValue}`, description: '', color: undefined }]);
    setEditing(prev => ({ ...prev, [nextValue]: { label: `Stage ${nextValue}`, value: nextValue } }));
  };

  const handleSaveAll = async () => {
    if (!orgId) return;
    try {
      setSaving(true);
      setError(null);
      // Merge any in-progress edits into the stages array for saving
      const stagedToSave = stages.map((s, idx) => {
        const pending = editing[s.value];
        const label = pending ? (pending.label.trim() || `Stage ${s.value}`) : s.label;
        return {
          value: s.value,
          label,
          description: s.description || null,
          color: s.color || null,
          order_index: idx,
        };
      });
      // Validate unique values
      const values = stagedToSave.map(s => s.value);
      const uniqueValues = new Set(values);
      if (uniqueValues.size !== values.length) {
        setError('Duplicate stage values detected. Please ensure each stage has a unique value.');
        return;
      }
      await progressStageSettingsService.replaceAllStages(orgId, stagedToSave as any);
      await loadData();
      setEditing({});
      setSuccess(true);
      setHasCustomSettings(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e: any) {
      setError(e.message || 'Failed to save progress stages');
    } finally {
      setSaving(false);
    }
  };

  // Drag-and-drop reorder (HTML5)
  const onDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const onDrop = (index: number) => {
    if (draggedIndex === null || draggedIndex === index) {
      setDraggedIndex(null);
      return;
    }
    setStages(prev => {
      const copy = [...prev];
      const [removed] = copy.splice(draggedIndex, 1);
      copy.splice(index, 0, removed);
      return copy;
    });
    setDraggedIndex(null);
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        mt: 3,
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
          Progress Stage Settings
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {orgId ? `Organization ID: ${orgId}` : ''}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {!hasCustomSettings && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You are currently viewing default progress stage settings. Click "Save Changes" to save these as custom settings for your organization.
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Progress stages updated successfully!
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Edit the names of your progress stages, or add/remove stages. Changes are saved per organization.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {stages.map((stage, idx) => {
              const isEditing = !!editing[stage.value];
              return (
                <Paper
                  key={`${stage.value}-${idx}`}
                  elevation={1}
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    cursor: 'grab',
                  }}
                  draggable
                  onDragStart={() => onDragStart(idx)}
                  onDragOver={onDragOver}
                  onDrop={() => onDrop(idx)}
                >
                  <DragIndicatorIcon sx={{ opacity: 0.7 }} />
                  <Chip
                    label={stage.value}
                    sx={{ width: 40, justifyContent: 'center', fontWeight: 700 }}
                    color="default"
                  />
                  {isEditing ? (
                    <>
                      <TextField
                        value={editing[stage.value]?.label || ''}
                        onChange={(e) => handleChange(stage.value, e.target.value)}
                        size="small"
                        fullWidth
                        label="Stage Label"
                        sx={{ mr: 1 }}
                      />
                      <TextField
                        value={editing[stage.value]?.value ?? stage.value}
                        onChange={(e) => handleChangeValue(stage.value, e.target.value)}
                        size="small"
                        label="Value"
                        type="number"
                        sx={{ width: 140 }}
                        inputProps={{ min: 0 }}
                      />
                    </>
                  ) : (
                    <Typography sx={{ fontWeight: 600, flex: 1 }}>{stage.label}</Typography>
                  )}
                  {isEditing ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={() => handleApplyEditLocal(stage.value)} disabled={saving}>
                        Done
                      </Button>
                      <Button size="small" onClick={() => handleCancel(stage.value)} disabled={saving}>
                        Cancel
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" variant="outlined" onClick={() => handleEdit(stage.value)}>
                        Edit
                      </Button>
                      <Tooltip title="Remove Stage">
                        <IconButton color="error" onClick={() => handleRemoveLocal(stage.value)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Box>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button startIcon={<AddIcon />} onClick={handleAdd} variant="outlined" disabled={saving}>
              Add Stage
            </Button>
            {onClose && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={onClose} startIcon={<CancelIcon />} disabled={saving}>
                  Close
                </Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveAll} disabled={saving || loading}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default ProgressStageSettings;


